/** Interaktive Elemente, auf denen kein Drag starten darf — sonst fängt das
 * Pointer-Capture den Klick ab, bevor er z.B. ein Auswahl-Dropdown öffnet. */
const INTERACTIVE_SELECTOR = "button, a, input, select, textarea, [role=\"button\"], [role=\"combobox\"], [role=\"option\"], [contenteditable=\"true\"]";

/**
 * Horizontales "Klick-und-Zieh"-Scrollen für breite Reihen (Schiffs-Slots,
 * Ausrüstungs-Spalten, ...), die bei vielen Einträgen/schmalen Bildschirmen
 * überlaufen. Pointer Events statt separater Maus-/Touch-Handler, damit
 * Maus, Touch und Stift einheitlich funktionieren.
 *
 * `onClickCapture` unterdrückt den Klick auf darunterliegende Elemente (z.B.
 * ein Auswahl-Dropdown), wenn der Pointer beim Loslassen tatsächlich bewegt
 * wurde — sonst würde ein Drag versehentlich als Klick gewertet.
 */
export function useDragScroll() {
  const containerRef = ref<HTMLElement | null>(null);
  const isDragging = ref(false);
  let startX = 0;
  let startScrollLeft = 0;
  let moved = false;

  function onPointerDown(event: PointerEvent) {
    if (!containerRef.value || event.button !== 0) return;
    // Klick landet auf einem Bedienelement (Dropdown, Button, ...) — dort
    // soll dessen eigenes Klick-/Öffnen-Verhalten greifen, kein Drag-Start.
    if ((event.target as HTMLElement | null)?.closest(INTERACTIVE_SELECTOR)) return;
    isDragging.value = true;
    moved = false;
    startX = event.clientX;
    startScrollLeft = containerRef.value.scrollLeft;
    containerRef.value.setPointerCapture(event.pointerId);
  }

  function onPointerMove(event: PointerEvent) {
    if (!isDragging.value || !containerRef.value) return;
    const delta = event.clientX - startX;
    if (Math.abs(delta) > 3) moved = true;
    containerRef.value.scrollLeft = startScrollLeft - delta;
  }

  function onPointerUp(event: PointerEvent) {
    isDragging.value = false;
    containerRef.value?.releasePointerCapture(event.pointerId);
  }

  function onClickCapture(event: MouseEvent) {
    if (!moved) return;
    event.stopPropagation();
    event.preventDefault();
  }

  return { containerRef, isDragging, onPointerDown, onPointerMove, onPointerUp, onClickCapture };
}
